import { useRef, type Ref } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { Button, Tag } from '../../components/primitives';
import {
  ROLE_LIST,
  TYPING_RULE_TEXT,
  type DemoTab
} from './landingRoleRulesDemoData';
import { LandingMockPointer } from './LandingMockPointer';
import { useRoleRulesLoop, type FlyVisual } from './useRoleRulesLoop';

type RoleRulesDemoBodyProps = {
  stageRef: Ref<HTMLDivElement> | null;
  saveRef: Ref<HTMLSpanElement> | null;
  historyTabRef: Ref<HTMLButtonElement> | null;
  ruleText: string;
  activeTab: DemoTab;
  isSaving: boolean;
  showPropagate: boolean;
  showFly: boolean;
  flyVisual: FlyVisual;
  saveHighlight: boolean;
  historyRipple: boolean;
  pointerClicking: boolean;
  showCursor: boolean;
  charCount: number;
  staticMode: boolean;
  pointerX: number;
  pointerY: number;
  showPointer: boolean;
};

function RoleRulesDemoBody({
  stageRef,
  saveRef,
  historyTabRef,
  ruleText,
  activeTab,
  isSaving,
  showPropagate,
  showFly,
  flyVisual,
  saveHighlight,
  historyRipple,
  pointerClicking,
  showCursor,
  charCount,
  staticMode,
  pointerX,
  pointerY,
  showPointer
}: RoleRulesDemoBodyProps) {
  const content = (
    <>
      <header className="set-fr-head">
        <h2 className="set-fr-title">Agent rules per role</h2>
        <p className="set-fr-lede">
          Define what each role does and the agent rule MCP loads for that role.
        </p>
      </header>

      <motion.div className="set-fr-grid lp-rules-fr-grid" layout={false}>
        <aside className="set-fr-list">
          <div className="set-fr-section">
            <div className="set-fr-section-head lp-rules-mono">System</div>
            {ROLE_LIST.map((role) => (
              <button
                key={role.key}
                type="button"
                className="pn-nav-list-item"
                data-selected={role.selected ? 'true' : 'false'}
                aria-current={role.selected ? 'true' : undefined}
                tabIndex={-1}
              >
                <span className="pn-nav-list-item-label">{role.label}</span>
                <span className="pn-nav-list-item-meta">{role.meta}</span>
              </button>
            ))}
          </div>
        </aside>

        <div className="set-fr-editor lp-rules-editor">
          <div className="set-fr-editor-body lp-rules-editor-body">
            <div className="set-fr-editor-head">
              <Tag>System</Tag>
              <h3 className="set-fr-editor-title">Engineering</h3>
              {staticMode && showPropagate ? (
                <span
                  className="lp-rules-propagating lp-rules-mono"
                  data-testid="lp-mock-fr-propagating"
                >
                  Propagating to engineers
                </span>
              ) : null}
            </div>

            <div className="pn-tabs" role="tablist" aria-label="Agent rule editor tabs">
              <button
                type="button"
                className="pn-tabs-tab"
                aria-selected={activeTab === 'edit'}
                tabIndex={-1}
              >
                Edit
              </button>
              <button
                type="button"
                className="pn-tabs-tab"
                aria-selected={activeTab === 'preview'}
                tabIndex={-1}
              >
                Preview
              </button>
              <button
                ref={historyTabRef ?? undefined}
                type="button"
                className={[
                  'pn-tabs-tab',
                  historyRipple ? 'lp-rules-history-tab--impact' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-selected={false}
                data-testid="lp-mock-fr-history-tab"
                tabIndex={-1}
              >
                History
                {historyRipple ? (
                  <span className="lp-rules-tab-ripple" aria-hidden="true" />
                ) : null}
              </button>
            </div>

            <label className="pn-field-label" htmlFor="lp-mock-fr-rule">
              Agent rule (markdown)
            </label>
            <pre
              className="lp-rules-faux-textarea lp-rules-rule-field set-fr-rule-field"
              id="lp-mock-fr-rule"
            >
              {ruleText}
              {showCursor ? <span className="lp-rules-cursor" aria-hidden="true" /> : null}
            </pre>

            <div className="lp-rules-char-row">
              <span />
              <span className="lp-rules-mono">{charCount} chars</span>
            </div>

            <div className="lp-rules-editor-actions">
              <Button type="button" variant="ghost" size="sm" tabIndex={-1}>
                Cancel
              </Button>
              <span ref={saveRef ?? undefined} className="lp-rules-save-anchor">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  tabIndex={-1}
                  data-demo-active={saveHighlight ? 'true' : undefined}
                  data-testid="lp-mock-fr-save"
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </Button>
              </span>
            </div>

            {!staticMode && showPropagate ? (
              <p
                className="lp-rules-propagating lp-rules-propagating--live lp-rules-mono"
                data-testid="lp-mock-fr-propagating"
              >
                Propagating to engineers
              </p>
            ) : null}
          </div>
        </div>
      </motion.div>

      {showFly ? (
        <motion.div
          className="lp-rules-save-fly"
          aria-hidden="true"
          style={{
            left: flyVisual.x,
            top: flyVisual.y,
            width: flyVisual.width,
            height: flyVisual.height,
            opacity: flyVisual.opacity,
            filter: `blur(${flyVisual.blurPx}px)`,
            transform: `scale(${flyVisual.scale})`
          }}
          initial={false}
        >
          Saved
        </motion.div>
      ) : null}

      <LandingMockPointer
        visible={showPointer}
        x={pointerX}
        y={pointerY}
        clicking={pointerClicking}
      />
    </>
  );

  if (stageRef) {
    return (
      <motion.div ref={stageRef} className="lp-rules-demo-stage" layout={false}>
        {content}
      </motion.div>
    );
  }

  return <div className="lp-rules-demo-stage">{content}</div>;
}

export function LandingRoleRulesStatic() {
  return (
    <motion.div className="lp-rules-mock" layout={false}>
      <RoleRulesDemoBody
        stageRef={null}
        saveRef={null}
        historyTabRef={null}
        ruleText={TYPING_RULE_TEXT}
        activeTab="edit"
        isSaving={false}
        showPropagate
        showFly={false}
        flyVisual={{
          x: 0,
          y: 0,
          width: 52,
          height: 22,
          opacity: 0,
          blurPx: 0,
          scale: 1
        }}
        saveHighlight={false}
        historyRipple={false}
        pointerClicking={false}
        showCursor={false}
        charCount={TYPING_RULE_TEXT.length}
        staticMode
        pointerX={0}
        pointerY={0}
        showPointer={false}
      />
    </motion.div>
  );
}

function LandingRoleRulesAnimated() {
  const stageWrapRef = useRef<HTMLDivElement>(null);
  const inView = useInView(stageWrapRef, { amount: 0.2, once: false });
  const loop = useRoleRulesLoop(inView);

  return (
    <div ref={stageWrapRef} className="lp-rules-mock">
      <RoleRulesDemoBody {...loop} staticMode={false} />
    </div>
  );
}

export function LandingRoleRulesDemo() {
  const reducedMotion = useReducedMotion();
  if (reducedMotion) {
    return <LandingRoleRulesStatic />;
  }
  return <LandingRoleRulesAnimated />;
}

